// API endpoints
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    FORGOT_PASSWORD: '/auth/password/forgot',
    RESET_PASSWORD: '/auth/password/reset',
    PROFILE: '/profile',
    SUBSCRIPTION_PARAMETERS: '/me/subscription-parameters',
    CHANGE_PASSWORD: '/profile/password/change',
  },
  DICTIONARIES: '/dictionaries',
  WORDS: '/words',
  TAGS: '/tags',
  SUBSCRIPTIONS: {
    PLANS: '/subscriptions-plans',
    USER: '/subscriptions',
    CURRENT: '/subscriptions/current',
  },
  PAYMENTS: '/payments',
  USERS: '/users',
  USERS_TEACHER: '/users/teacher',
  GROUPS: '/groups',
  CARDS: '/cards',
  INVITATIONS: '/invitations',
  USER_SETTINGS: '/user-settings',
  HELP: '/help',
  HELP_BY_ID: (id: number) => `/help/${id}`,
  HELP_ATTACHMENTS: (id: number) => `/help/${id}/attachments`,
  HELP_ATTACHMENT: (id: number, attachmentId: number) =>
    `/help/${id}/attachments/${attachmentId}`,
  OAUTH: {
    AUTHORIZE_REQUEST: '/oauth/authorize-request',
    AUTHORIZE_APPROVE: '/oauth/authorize-approve',
    AUTHORIZE_DENY: '/oauth/authorize-deny',
    CONNECTIONS: '/oauth/connections',
    CONNECTION: (id: string) => `/oauth/connections/${id}`,
  },
  ADMIN: {
    USERS: '/admin/users',
    SUBSCRIPTIONS_EXTEND_ALL: '/admin/subscriptions/extend-all',
    BALANCE_TOPUP_REQUESTS: '/admin/balance-topup-requests',
    BALANCE_TOPUP_CONFIRM: (id: number) => `/admin/balance-topup-requests/${id}/confirm`,
    BALANCE_TOPUP_REJECT: (id: number) => `/admin/balance-topup-requests/${id}/reject`,
    USER_BALANCE_ADJUST: (userId: number) => `/admin/users/${userId}/balance/adjust`,
    FINANCE: {
      PAYMENTS: '/admin/payments',
      PAYMENT_REFUND: (id: number) => `/admin/payments/${id}/refund`,
      PAYMENT_MARK_FAILED: (id: number) => `/admin/payments/${id}/mark-failed`,
      PAYMENT_MARK_SUCCEEDED: (id: number) => `/admin/payments/${id}/mark-succeeded`,
      CONTRACT_TRANSACTIONS: '/admin/contract-transactions',
      BALANCE_TRANSACTIONS: '/admin/balance-transactions',
    },
    NEWS: {
      LIST: '/admin/news',
      ITEM: (id: number) => `/admin/news/${id}`,
      PUBLISH: (id: number) => `/admin/news/${id}/publish`,
      UNPUBLISH: (id: number) => `/admin/news/${id}/unpublish`,
      COVER: (id: number) => `/admin/news/${id}/cover`,
      IMAGES: '/admin/news/images',
      PREVIEW_LINK: (id: number) => `/admin/news/${id}/preview-link`,
      READS: (id: number) => `/admin/news/${id}/reads`,
      TAGS: '/admin/news-tags',
      TAG: (id: number) => `/admin/news-tags/${id}`,
    },
    CHAT_STICKERS: {
      PACKS: '/admin/chat/sticker-packs',
      PACK: (id: number) => `/admin/chat/sticker-packs/${id}`,
      PACK_STICKERS: (packId: number) => `/admin/chat/sticker-packs/${packId}/stickers`,
      PACK_STICKERS_REORDER: (packId: number) =>
        `/admin/chat/sticker-packs/${packId}/stickers/reorder`,
      STICKER: (id: number) => `/admin/chat/stickers/${id}`,
    },
  },
  LANDING: {
    NEWS: '/landing/news',
    NEWS_ITEM: (slug: string) => `/landing/news/${slug}`,
    NEWS_READ: (slug: string) => `/news/${slug}/read`,
  },
  TTS: {
    GENERATE: '/words/generate-audio',
    ESTIMATE: '/tts/estimate',
    BATCHES: '/tts/batches',
    BATCH: (id: number) => `/tts/batches/${id}`,
  },
  TEACHER_BALANCE: {
    SHOW: '/teacher/balance',
    TRANSACTIONS: '/teacher/balance/transactions',
    TOPUP_REQUESTS: '/teacher/balance/topup-requests',
    TOPUP_CANCEL: (id: number) => `/teacher/balance/topup-requests/${id}/cancel`,
  },
  ACCOUNTING: {
    TAX_SETTINGS: '/teacher/tax-settings',
    EXPENSES: '/teacher/expenses',
    EXPENSE: (id: number) => `/teacher/expenses/${id}`,
    INCOME_REPORT: '/teacher/income/report',
    INCOME_REPORT_CSV: '/teacher/income/report.csv',
    SUMMARY: '/teacher/accounting/summary',
  },
  LESSONS: '/lessons',
  LESSON_API: {
    CALCULATE_PRICE: '/lessons/calculate-price',
    PAYMENT: (lessonId: number) => `/lessons/${lessonId}/payment`,
  },
  GROUP_BILLING: {
    LESSON_SUBSCRIPTIONS: (groupId: number) => `/groups/${groupId}/lesson-subscriptions`,
    LESSON_SUBSCRIPTION_BATCH: (groupId: number, batchId: string) =>
      `/groups/${groupId}/lesson-subscriptions/${batchId}`,
  },
  HOMEWORKS: {
    USER: (userId: number) => `/users/${userId}/homeworks`,
    GROUP: (groupId: number) => `/groups/${groupId}/homeworks`,
  },
  LESSON_NOTES: {
    FOR_LESSON: (lessonId: number) => `/lessons/${lessonId}/notes`,
    UPDATE_NOTE: (noteId: number) => `/lesson-notes/${noteId}`,
  },
  LESSON_SCHEDULES: {
    USER: (userId: number) => `/users/${userId}/lesson-schedules`,
    GROUP: (groupId: number) => `/groups/${groupId}/lesson-schedules`,
  },
  CONTRACTS: {
    BALANCE: (contractId: number) => `/contracts/${contractId}/balance`,
    BILLING_SETTINGS: (contractId: number) => `/contracts/${contractId}/billing-settings`,
    TRANSACTIONS: (contractId: number) => `/contracts/${contractId}/transactions`,
    TRANSACTIONS_TOPUP: (contractId: number) => `/contracts/${contractId}/transactions/topup`,
    TRANSACTIONS_CORRECTION: (contractId: number) => `/contracts/${contractId}/transactions/correction`,
    TRANSACTIONS_STUDENT_REQUEST: (contractId: number) => `/contracts/${contractId}/transactions/student-request`,
    LESSON_PRICES: (contractId: number) => `/contracts/${contractId}/lesson-prices`,
    LESSON_SUBSCRIPTIONS: (contractId: number) => `/contracts/${contractId}/lesson-subscriptions`,
    LESSON_SUBSCRIPTIONS_REQUEST: (contractId: number) => `/contracts/${contractId}/lesson-subscriptions/request`,
    AVAILABLE_PLANS: (contractId: number) => `/contracts/${contractId}/available-plans`,
  },
  GROUPS_PRICING: {
    /** Цена часа группового занятия: своя ступень между ценой участника и ставкой учителя. */
    LESSON_PRICES: (groupId: number) => `/groups/${groupId}/lesson-prices`,
  },
  CONTRACT_TRANSACTIONS: {
    APPROVE: (transactionId: number) => `/contract-transactions/${transactionId}/approve`,
    REJECT: (transactionId: number) => `/contract-transactions/${transactionId}/reject`,
  },
  TEACHER_PRICES: '/teacher-prices',
  LESSON_SUBSCRIPTION_PLANS: '/lesson-subscription-plans',
  LESSON_SUBSCRIPTION_PLAN: (planId: number) => `/lesson-subscription-plans/${planId}`,
  LESSON_SUBSCRIPTION_GRANT_CANCEL: (grantId: number) => `/lesson-subscriptions/${grantId}/cancel`,
  LESSON_SUBSCRIPTION_GRANT_CONSUMPTIONS: (grantId: number) => `/lesson-subscriptions/${grantId}/consumptions`,
  CHAT: {
    ROOMS: '/chat/rooms',
    CONTACTS: '/chat/contacts',
    OPEN_DIRECT: (userId: number) => `/chat/rooms/direct/${userId}`,
    MESSAGES: (roomId: number) => `/chat/rooms/${roomId}/messages`,
    READ: (roomId: number) => `/chat/rooms/${roomId}/read`,
    UPLOAD: (roomId: number) => `/chat/rooms/${roomId}/upload`,
    MUTE: (roomId: number) => `/chat/rooms/${roomId}/mute`,
    MESSAGE: (messageId: number) => `/chat/messages/${messageId}`,
    PRESENCE: (roomId: number) => `/chat/rooms/${roomId}/presence`,
    REACTIONS: (messageId: number) => `/chat/messages/${messageId}/reactions`,
    STICKER_PACKS: '/chat/sticker-packs',
  },
  NOTIFICATIONS: {
    LIST: '/notifications',
    READ: (id: string) => `/notifications/${id}/read`,
    MARK_ALL_READ: '/notifications/mark-all-read',
    PREFERENCES: '/profile/notification-preferences',
  },
  PUSH: {
    VAPID_PUBLIC_KEY: '/push/vapid-public-key',
    SUBSCRIPTIONS: '/push/subscriptions',
    SUBSCRIPTION: (id: number) => `/push/subscriptions/${id}`,
  },
  DASHBOARD: {
    WIDGETS: '/dashboard/widgets',
    LAYOUT: '/dashboard/layout',
    LAYOUT_RESET: '/dashboard/layout/reset',
    TEACHER: '/dashboard/teacher',
    STUDENT: '/dashboard/student',
    HEATMAP: '/dashboard/widgets/heatmap',
  },
  /** Лента активности: свой журнал, ученика (учитель), любой (админ) */
  USER_ACTIVITY: '/user/activity',
} as const

// Local storage keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  ADMIN_TOKEN: 'admin_token',
  TOKEN_EXPIRES_AT: 'token_expires_at',
  LANGUAGE: 'language',
  THEME: 'theme',
} as const

// Routes
export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  DASHBOARD: '/dashboard',
  DICTIONARIES: '/dictionaries',
  /** Страница словаря по id */
  DICTIONARY_VIEW: (id: number) => `/dictionaries/${id}`,
  WORDS: '/words',
  TAGS: '/tags',
  PROFILE: '/profile',
  /** Обращение в поддержку (Plane Intake) */
  HELP: '/help',
  PROFILE_SUBSCRIPTION: '/profile/subscription',
  PROFILE_PLANS: '/profile/subscription/change',
  PROFILE_SETTINGS: '/profile/settings',
  PROFILE_NOTIFICATIONS: '/profile/notifications',
  PROFILE_SECURITY: '/profile/security',
  /** Подключённые AI-приложения (MCP) */
  PROFILE_CONNECTIONS: '/profile/connections',
  /** Экран согласия на подключение AI-агента по OAuth */
  OAUTH_AUTHORIZE: '/oauth/authorize',
  /** Учитель: какие поля входят в его анкету и какие из них обязательны */
  PROFILE_STUDENT_FORM_FIELDS: '/profile/student-form-fields',
  PROFILE_BILLING: '/profile/billing',
  /** Рублёвый баланс учителя (TTS и AI-функции) */
  PROFILE_BALANCE: '/profile/balance',
  FINANCE: '/finance',
  /** Бухгалтерия учителя: доходы, налоги, расходы, сводка */
  ACCOUNTING: '/accounting',
  VACATIONS: '/vacations',
  USERS: '/users',
  /** Настройки ученика (для учителя) */
  USER_SETTINGS: (id: number) => `/users/${id}/settings`,
  /** Активность ученика (для учителя) */
  USER_ACTIVITY: (id: number) => `/users/${id}/activity`,
  /** Своя активность */
  PROFILE_ACTIVITY: '/profile/activity',
  /** Админ: активность пользователя */
  ADMIN_USER_ACTIVITY: (id: number) => `/admin/users/${id}/activity`,
  /** Финансы ученика (для учителя) */
  USER_FINANCE: (id: number) => `/users/${id}/finance`,
  GROUPS: '/groups',
  CARDS: '/cards',
  CARDS_VIEW: (id: number) => `/cards/${id}`,
  USER_CARDS: (id: number) => `/users/${id}/cards`,
  USER_CARD_VIEW: (userId: number, cardId: number) => `/users/${userId}/cards/${cardId}`,
  /** Создание прогонов «Пополнить словарь» из урока (ученики = участники урока) */
  LESSON_DICTIONARY_FILL_CREATE: (lessonId: number) => `/lessons/${lessonId}/dictionary-fill/create`,
  DICTIONARY_FILL_RUN: (runId: number) => `/dictionary-fill/${runId}`,
  /** Страница слов с опциональным id слова в query (для учителя/админа) */
  WORD_VIEW: (wordId: number) => `/words?word_id=${wordId}`,
  /** Задачи озвучивания слов (очередь TTS-батчей) */
  TTS_TASKS: '/tts-tasks',
  /** Страница «Мой учитель» для ученика */
  TEACHER: '/teacher',
  /** Админ: список пользователей */
  ADMIN_USERS: '/admin/users',
  /** Админ: просмотр пользователя */
  ADMIN_USER_VIEW: (id: number) => `/admin/users/${id}`,
  /** Админ: платежи */
  ADMIN_PAYMENTS: '/admin/payments',
  /** Админ: финансовый журнал (платформа/контракты/баланс) */
  ADMIN_FINANCE: '/admin/finance',
  /** Админ: подписки */
  ADMIN_SUBSCRIPTIONS: '/admin/subscriptions',
  /** Админ: тарифные планы подписки */
  ADMIN_PLANS: '/admin/subscription-plans',
  /** Админ: заявки на пополнение баланса учителей */
  ADMIN_TOPUP_REQUESTS: '/admin/topup-requests',
  /** Админ: новости лэндинга */
  ADMIN_NEWS: '/admin/news',
  /** Админ: системный каталог полей анкеты */
  ADMIN_STUDENT_FORM_FIELDS: '/admin/student-form-fields',
  /** Админ: наборы стикеров чата */
  ADMIN_CHAT_STICKERS: '/admin/chat-stickers',
  /** Админ: стикеры конкретного набора */
  ADMIN_CHAT_STICKER_PACK: (packId: number) => `/admin/chat-stickers/${packId}`,
  /** Список всех новостей */
  NEWS: '/news',
  /** Новость по slug: открывает список и модалку поверх (deep-link из соцсетей) */
  NEWS_VIEW: (slug: string) => `/news/${slug}`,
  /** Программы обучения (учитель): библиотека шаблонов */
  PROGRAMS: '/programs',
  /** Каталог системных программ платформы (админ) */
  ADMIN_PROGRAMS: '/admin/programs',
  /** Банк калиброванных заданий теста уровня (админ) */
  ADMIN_PLACEMENT_BANK: '/admin/placement-bank',
  /** Тест уровня: проходит ученик за себя */
  PLACEMENT: '/placement',
  // Тот же тест, но полноэкранным мастером: первый экран ученика, пришедшего
  // самостоятельно. В кабинет он попадает уже после него.
  ONBOARDING: '/onboarding',
  // Свой уровень у ученика: та же страница, что у учителя, но по своему адресу.
  // Под /users/{id}/… кабинет переключается в учительский контекст просмотра
  // ученика — ученик видел бы меню «Настройки ученика», «Финансы» и прочее,
  // адресованное не ему.
  MY_LEVEL: '/my-level',
  /** Конструктор программы */
  PROGRAM_VIEW: (id: number) => `/programs/${id}`,
  /** Карточка назначения программы ученику или группе */
  PROGRAM_ASSIGNMENT: (id: number) => `/program-assignments/${id}`,
  /** Программы ученика (дорожная карта) */
  MY_PROGRAMS: '/my/programs',
  /** Одна программа ученика */
  MY_PROGRAM_VIEW: (id: number) => `/my/programs/${id}`,
  /** Решение интерактивного задания (попытка) */
  EXERCISE_RUN: (runId: number) => `/exercise-runs/${runId}`,
  /** Очередь заданий на проверке (учитель) */
  /** Банк заданий учителя (заменил страницу диктантов) */
  EXERCISE_BANK: '/exercises',
  EXERCISE_REVIEW: '/exercises/review',
  /** Проверка одной сданной работы (учитель) */
  EXERCISE_REVIEW_RUN: (runId: number) => `/exercises/review/${runId}`,
  /** Уроки (ученик; при комбинированных ролях — список ученика) */
  LESSONS: '/lessons',
  /** Детали урока (учитель/участник) */
  LESSON_DETAILS: (id: number) => `/lessons/${id}`,
  /** Расписание ученика (просмотр; редактирование — в настройках ученика, модалка) */
  MY_SCHEDULE: '/schedule',
  /** Домашние задания (ученик/учитель) */
  HOMEWORKS: '/homeworks',
  /** Домашние задания конкретного ученика (учитель) */
  USER_HOMEWORKS: (id: number) => `/users/${id}/homeworks`,
  /** Проверка одного пункта домашки (учитель) */
  HOMEWORK_ITEM_CHECK: (userId: number, homeworkId: number, itemId: number) =>
    `/users/${userId}/homeworks/${homeworkId}/items/${itemId}/check`,
  /** Тесты ученика (учитель) */
  USER_TESTS: (userId: number) => `/users/${userId}/tests`,
  USER_LANGUAGE_PROFILE: (userId: number) => `/users/${userId}/language-profile`,
  USER_PLACEMENT_RUN: (userId: number, runId: number) =>
    `/users/${userId}/language-profile/runs/${runId}`,
  USER_PROGRAMS: (id: number) => `/users/${id}/programs`,
  USER_PROGRAM_ASSIGNMENT: (userId: number, assignmentId: number) => `/users/${userId}/programs/${assignmentId}`,
  /** Мои тесты (ученик) */
  TESTS: '/tests',
  /** Создание self-теста (ученик) */
  SELF_TEST_CREATE: '/tests/self/create',
  /** Создание теста из урока (учитель) */
  LESSON_TEST_CREATE: (lessonId: number) => `/lessons/${lessonId}/tests/create`,
  /** Fullscreen тест */
  TEST_RUN: (id: number) => `/tests/${id}`,
  /** Чат: список комнат / общий экран */
  CHAT: '/chat',
  /** Чат: открыть комнату по id (deep-link из уведомлений) */
  CHAT_ROOM: (roomId: number) => `/chat/rooms/${roomId}`,
  /** Чат: открыть direct-комнату с пользователем */
  CHAT_DIRECT: (userId: number) => `/chat/direct/${userId}`,
  /** Чат: открыть групповую комнату */
  CHAT_GROUP: (groupId: number) => `/chat/group/${groupId}`,
  NOTIFICATIONS: '/notifications',
} as const
